<template>
  <div class="sp-table" :class="classModifiers" part="base">
    <slot name="top"></slot>

    <div class="sp-table__viewport" part="viewport" tabindex="0" role="group" :aria-label="caption">
      <table class="sp-table__table" part="table" :aria-busy="ariaBusy">
        <caption v-if="caption" class="sp-table__caption">
          {{
            caption
          }}
        </caption>

        <TableHeadRow
          :columns="columns"
          :sort-field="sortField"
          :direction="sortDirection"
          :sticky-header="isSticky"
          :expandable="isExpandable"
        >
          <!-- A slot projects the host's light DOM only where the registered element declares it,
               so every header slot is declared here, default content and all, and handed down. -->
          <template v-for="column in columns" :key="column.field" #[`header-${column.field}`]>
            <slot :name="`header-${column.field}`">
              <TableSortButton
                v-if="column.sortable"
                :label="labelFor(column)"
                :active="sortField === column.field"
                :direction="sortDirection"
                @sort="onSort(column)"
              />
              <template v-else>{{ labelFor(column) }}</template>
            </slot>
          </template>
        </TableHeadRow>

        <TableBodyRows
          :columns="columns"
          :items="visibleItems"
          :row-key="rowKey"
          :row-attrs="rowAttrs"
          :hover="isHoverable"
          :striped="isStriped"
          :loading="isLoading"
          :expansion="expansion"
          :expanded-keys="expandedKeys"
          @row-click="emit('row-click', $event)"
          @toggle-expanded="toggleExpanded"
        >
          <template #empty>
            <slot name="empty">{{ emptyText }}</slot>
          </template>
        </TableBodyRows>
      </table>
    </div>

    <slot name="pager">
      <TableFooter
        v-if="isPaginated"
        :variant="pagerVariant"
        :page="page"
        :page-count="pageCount"
        :per-page="perPage"
        :per-page-choices="perPageChoices"
        :is-first-page="isFirstPage"
        :is-last-page="isLastPage"
        @page-change="emit('page-change', $event)"
      />
    </slot>

    <slot name="bottom"></slot>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

import { t } from "#ce/shared/components-i18n";
import { isPresent } from "#ce/utils/props";
import TableBodyRows from "./components/TableBodyRows.ce.vue";
import TableFooter from "./components/TableFooter.ce.vue";
import TableHeadRow from "./components/TableHeadRow.ce.vue";
import TableSortButton from "./components/TableSortButton.ce.vue";
import { useTablePagination } from "./composables/useTablePagination.js";
import { useTableSort } from "./composables/useTableSort.js";
import { rowKeyFor } from "./columns.js";
import { props as tableProps } from "./props.js";

const props = defineProps(tableProps);

const emit = defineEmits(["row-click", "sort-change", "page-change", "expand-change"]);

const isHoverable = computed(() => isPresent(props.hover));
const isStriped = computed(() => isPresent(props.striped));
const isSticky = computed(() => isPresent(props.stickyHeader));
const isLoading = computed(() => isPresent(props.loading));
const isExpandable = computed(() => typeof props.expansion === "function");

const expandedKeys = ref(new Set());

const {
  field: sortField,
  direction: sortDirection,
  sortedItems,
  nextSortFor,
} = useTableSort({
  columns: () => props.columns,
  items: () => props.items,
  sort: () => props.sort,
  clientSort: () => isPresent(props.clientSort),
  sortFn: () => props.sortFn,
});

const { page, perPage, pageCount, isPaginated, isFirstPage, isLastPage, visibleItems, perPageChoices } =
  useTablePagination({
    items: sortedItems,
    page: () => props.page,
    perPage: () => props.perPage,
    total: () => props.total,
    clientPaginate: () => isPresent(props.clientPaginate),
    perPageOptions: () => props.perPageOptions,
  });

const emptyText = computed(() => t("table_empty"));
const ariaBusy = computed(() => (isLoading.value ? "true" : undefined));

const classModifiers = computed(() => ({
  "--hover": isHoverable.value,
  "--striped": isStriped.value,
  "--sticky-header": isSticky.value,
  "--loading": isLoading.value,
}));

function labelFor(column) {
  return column.label ?? column.field;
}

function onSort(column) {
  emit("sort-change", nextSortFor(column));
}

function toggleExpanded({ item, index }) {
  const key = rowKeyFor(item, index, props.rowKey);
  const next = new Set(expandedKeys.value);
  const expanded = !next.has(key);

  if (expanded) {
    next.add(key);
  } else {
    next.delete(key);
  }

  expandedKeys.value = next;
  emit("expand-change", { item, index, expanded });
}
</script>

<style>
/* A height or max-height on the element bounds the table, so the rows scroll under a sticky header. */
:host {
  display: flex;
  flex-direction: column;
}
</style>

<style lang="scss" scoped>
.sp-table {
  // Read by every child through inheritance; they render the cells these describe.
  --table-border-color: var(--sp-comp-table-border-color, var(--sp-sys-color-outline-variant));
  --table-header-color: var(--sp-comp-table-header-color, var(--sp-sys-color-on-surface-variant));
  --table-cell-padding-block: var(--sp-comp-table-cell-padding-block, var(--sp-ref-scale-12));
  --table-cell-padding-inline: var(--sp-comp-table-cell-padding-inline, var(--sp-ref-scale-12));

  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--sp-comp-table-gap, var(--sp-ref-scale-8));
  min-height: 0;
  // Declared rather than inherited: a host that sets no font of its own leaves the shadow content
  // on the browser default.
  font-family: var(--sp-comp-table-font-family, var(--sp-sys-typescale-body-font-family));

  &__viewport {
    position: relative;

    &:focus-visible {
      outline: var(--sp-comp-table-focus-outline-width, var(--sp-sys-state-focus-indicator-thickness)) solid
        var(--sp-comp-table-focus-outline-color, var(--sp-sys-state-focus-indicator-color));
      outline-offset: -1px;
    }
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    border: var(
        --sp-comp-table-frame-border-width,
        var(--sp-comp-table-border-width, var(--sp-sys-border-width-default))
      )
      solid var(--table-border-color);
    border-radius: var(--sp-comp-table-border-radius, var(--sp-ref-scale-4));
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sp-comp-table-font-size, var(--sp-ref-font-size-small));
  }

  &__caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
}
</style>
